import { FEATURE_FLAGS } from '@belong/common';
import {
  EmployeeAssignmentDetailModel,
  EmployeeAssignmentModel,
} from 'api/models';

export const getInspectionType = (
  flow: any,
):
  | {
      isAdoptedLease: boolean;
      isOnboarding: boolean;
      isMoveOut: boolean;
      isPreMoveOut: boolean;
      isReadinessWalkthrough: boolean;
    }
  // The guard below returns `undefined`, which the declared type used to omit.
  | undefined => {
  const { flowSubType, isAdoptedLease } = flow?.configuration || {};
  if (!flowSubType) return;
  return {
    isAdoptedLease,
    isOnboarding: flowSubType === 'Onboarding', // adopted lease comes as onboarding
    isMoveOut: flowSubType === 'MoveOut',
    isPreMoveOut: flowSubType === 'PreMoveOut',
    isReadinessWalkthrough: flowSubType === 'QA',
  };
};

// The only reason to do that is because Lily asked to "come back" if the changes don't pay off.
// Besides, we don't currently support feature flags.
export const groupRecommendedImprovements = true;

export const QUERY_PARAMS = {
  REPORT_APPROVED: 'reportApproved',
};

export const getEmployeeAssignment = ({
  employeeAssignments,
  assignmentType,
}: {
  employeeAssignments?: EmployeeAssignmentDetailModel[];
  assignmentType?: EmployeeAssignmentModel['assignmentType'];
}) =>
  employeeAssignments?.find(
    (e) => e?.employeeAssignment?.assignmentType === assignmentType,
  ) || employeeAssignments?.[0];

export const getAssignedFormattedEmployee = ({
  employeeAssignments,
  assignmentTypes,
}: {
  employeeAssignments: EmployeeAssignmentDetailModel[];
  // Required, not optional: both call sites always pass it and the `.map` below
  // would throw without it.
  assignmentTypes: EmployeeAssignmentModel['assignmentType'][];
}) => {
  const assignedEmployeeByType = assignmentTypes
    .map((assignmentType) =>
      employeeAssignments.find(
        // Optional, matching `getEmployeeAssignment` directly above, which
        // already reads the same field through `?.`.
        (assignment) =>
          assignment.employeeAssignment?.assignmentType === assignmentType,
      ),
    )
    // Get the first assigned employee that is not undefined
    .find(Boolean);

  const firstAssignedEmployee = employeeAssignments[0];

  // if we don't find any assigned employee for the assignment types
  // return the first assigned employee of the home
  // The assertions below say what this function has always assumed: that the
  // home has at least one assignment, and that it carries an employee and a
  // user. An empty or partial list threw here before `noUncheckedIndexedAccess`
  // made it visible, and still does — the assertions are erased at runtime.
  const assignedEmployee = (assignedEmployeeByType ?? firstAssignedEmployee)!;

  return {
    ...assignedEmployee.employee,
    ...assignedEmployee.user,
    assignment: assignedEmployee.employeeAssignment,
    profileImage: assignedEmployee.user!.profileImageUrl,
    bannerImage: assignedEmployee.employee!.bannerImageUrl,
  };
};

type GetCanExclusivityBeSelectedParams = {
  isLegacyInvoicing: boolean;
  estimatedTotal: number;
  isOnboardingInspection: boolean;
  isDoNotExceedGroup: boolean;
};

export function getCanExclusivityBeSelected({
  isLegacyInvoicing,
  estimatedTotal,
  isOnboardingInspection,
  isDoNotExceedGroup,
}: GetCanExclusivityBeSelectedParams): boolean {
  if (!FEATURE_FLAGS.READY_TO_RENT) {
    return false;
  }

  if (
    !isLegacyInvoicing ||
    !isDoNotExceedGroup ||
    !isOnboardingInspection ||
    estimatedTotal >= 9000
  ) {
    return false;
  }

  return estimatedTotal > 0;
}
